import {
  BarChart3,
  Database,
  Download,
  Globe2,
  LoaderCircle,
  Plus,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  createCustomDomain,
  getBetaOverview,
  getSupportDiagnostics,
  importQodes,
  upsertHostedPage,
} from "../api";
import type {
  BetaOverview,
  QodeItem,
  SupportDiagnostics,
  WorkspaceCapabilities,
} from "../types";

type BetaOpsViewProps = {
  capabilities: WorkspaceCapabilities;
  qodes: QodeItem[];
  workspaceId: string;
  onRefreshQodes: () => Promise<void>;
};

const emptyPage = {
  qodeId: "",
  title: "",
  body: "",
  ctaLabel: "",
  ctaUrl: "",
  status: "draft" as const,
};

export function BetaOpsView({
  capabilities,
  qodes,
  workspaceId,
  onRefreshQodes,
}: BetaOpsViewProps) {
  const [overview, setOverview] = useState<BetaOverview | null>(null);
  const [domain, setDomain] = useState({ qodeId: "", hostname: "" });
  const [page, setPage] = useState(emptyPage);
  const [importText, setImportText] = useState("");
  const [diagnostics, setDiagnostics] = useState<SupportDiagnostics | null>(
    null,
  );
  const [working, setWorking] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setOverview(null);
    setError("");
    getBetaOverview()
      .then((response) => {
        if (active) setOverview(response);
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : "读取失败。");
        }
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  const run = async (key: string, action: () => Promise<void>) => {
    setWorking(key);
    setError("");
    try {
      await action();
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "操作未能完成。",
      );
    } finally {
      setWorking("");
    }
  };

  return (
    <div className="page beta-page">
      <section className="page-heading">
        <div>
          <h1>Beta Ops</h1>
          <p>管理真实团队试运行所需的域名、页面、数据与支持能力。</p>
        </div>
        <a className="secondary-button beta-export" href="/api/export">
          <Download size={14} />
          导出数据
        </a>
      </section>

      {error ? <div className="action-error">{error}</div> : null}

      {!overview ? (
        <div className="drawer-loading">
          <LoaderCircle className="spin" size={18} />
          正在读取 Beta 配置
        </div>
      ) : (
        <>
          <section className="beta-metrics">
            <div>
              <BarChart3 size={17} />
              <span>累计扫描</span>
              <strong>{overview.analytics.totalScans.toLocaleString()}</strong>
            </div>
            <div>
              <ShieldCheck size={17} />
              <span>在线 Qode</span>
              <strong>{overview.analytics.activeQodes}</strong>
            </div>
            <div>
              <Globe2 size={17} />
              <span>自定义域名</span>
              <strong>
                {overview.usage.customDomains}/{overview.quotas.customDomains}
              </strong>
            </div>
            <div>
              <Database size={17} />
              <span>托管页面</span>
              <strong>
                {overview.usage.hostedPages}/{overview.quotas.hostedPages}
              </strong>
            </div>
          </section>

          <section className="beta-grid">
            <div className="beta-panel">
              <div className="section-title">
                <div>
                  <h2>自定义域名</h2>
                  <span>登记后保持 Pending，完成 DNS 与人工验证才激活。</span>
                </div>
              </div>
              <div className="beta-form-row">
                <select
                  value={domain.qodeId}
                  onChange={(event) =>
                    setDomain((current) => ({
                      ...current,
                      qodeId: event.target.value,
                    }))
                  }
                >
                  <option value="">选择 Qode</option>
                  {qodes.map((qode) => (
                    <option value={qode.id} key={qode.id}>
                      {qode.name}
                    </option>
                  ))}
                </select>
                <input
                  value={domain.hostname}
                  placeholder="go.example.com"
                  onChange={(event) =>
                    setDomain((current) => ({
                      ...current,
                      hostname: event.target.value,
                    }))
                  }
                />
                <button
                  className="primary-button"
                  type="button"
                  disabled={
                    !capabilities.operate ||
                    !!working ||
                    !domain.qodeId ||
                    !domain.hostname
                  }
                  onClick={() =>
                    void run("domain", async () => {
                      const response = await createCustomDomain(domain);
                      setOverview(response);
                      setDomain({ qodeId: "", hostname: "" });
                    })
                  }
                >
                  {working === "domain" ? (
                    <LoaderCircle className="spin" size={14} />
                  ) : (
                    <Plus size={14} />
                  )}
                  登记
                </button>
              </div>
              <div className="beta-list">
                {overview.domains.map((item) => (
                  <div key={item.id}>
                    <span className={`beta-status beta-${item.status}`}>
                      {item.status}
                    </span>
                    <strong>{item.hostname}</strong>
                    <small>{item.qode.name}</small>
                    <code>{item.verificationToken}</code>
                  </div>
                ))}
                {!overview.domains.length ? <span>尚未登记域名</span> : null}
              </div>
            </div>

            <div className="beta-panel">
              <div className="section-title">
                <div>
                  <h2>托管轻页面</h2>
                  <span>发布后通过 /p/:slug 独立访问。</span>
                </div>
              </div>
              <div className="beta-page-form">
                <select
                  value={page.qodeId}
                  onChange={(event) =>
                    setPage((current) => ({
                      ...current,
                      qodeId: event.target.value,
                    }))
                  }
                >
                  <option value="">选择 Qode</option>
                  {qodes.map((qode) => (
                    <option value={qode.id} key={qode.id}>
                      {qode.name}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="页面标题"
                  value={page.title}
                  onChange={(event) =>
                    setPage((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
                <textarea
                  placeholder="页面正文"
                  value={page.body}
                  onChange={(event) =>
                    setPage((current) => ({
                      ...current,
                      body: event.target.value,
                    }))
                  }
                />
                <input
                  placeholder="按钮文字"
                  value={page.ctaLabel}
                  onChange={(event) =>
                    setPage((current) => ({
                      ...current,
                      ctaLabel: event.target.value,
                    }))
                  }
                />
                <input
                  placeholder="https://example.com"
                  value={page.ctaUrl}
                  onChange={(event) =>
                    setPage((current) => ({
                      ...current,
                      ctaUrl: event.target.value,
                    }))
                  }
                />
                <button
                  className="primary-button"
                  type="button"
                  disabled={
                    !capabilities.operate ||
                    !!working ||
                    Object.values(page).some((value) => !value)
                  }
                  onClick={() =>
                    void run("page", async () => {
                      const response = await upsertHostedPage({
                        ...page,
                        status: "published",
                      });
                      setOverview(response);
                      setPage(emptyPage);
                    })
                  }
                >
                  发布页面
                </button>
              </div>
            </div>

            <div className="beta-panel">
              <div className="section-title">
                <div>
                  <h2>批量导入</h2>
                  <span>JSON 数组，导入后统一进入 Draft。</span>
                </div>
              </div>
              <textarea
                className="beta-import"
                value={importText}
                placeholder='[{"name":"活动入口","purpose":"线下海报","destination":"https://example.com"}]'
                onChange={(event) => setImportText(event.target.value)}
              />
              <button
                className="secondary-button"
                type="button"
                disabled={!capabilities.operate || !!working || !importText}
                onClick={() =>
                  void run("import", async () => {
                    const parsed = JSON.parse(importText) as Parameters<
                      typeof importQodes
                    >[0];
                    await importQodes(parsed);
                    await onRefreshQodes();
                    setOverview(await getBetaOverview());
                    setImportText("");
                  })
                }
              >
                <Upload size={14} />
                导入 Draft
              </button>
            </div>

            <div className="beta-panel">
              <div className="section-title">
                <div>
                  <h2>支持诊断</h2>
                  <span>数据库完整性、外键与工作区对象计数。</span>
                </div>
              </div>
              <button
                className="secondary-button"
                type="button"
                disabled={!capabilities.manageMembers || !!working}
                onClick={() =>
                  void run("support", async () => {
                    setDiagnostics(await getSupportDiagnostics());
                  })
                }
              >
                <Database size={14} />
                运行诊断
              </button>
              {diagnostics ? (
                <div className="beta-diagnostics">
                  <strong>
                    {diagnostics.integrity === "ok" ? "Integrity OK" : "异常"}
                  </strong>
                  <span>外键异常 {diagnostics.foreignKeyViolations}</span>
                  <span>API {diagnostics.version}</span>
                </div>
              ) : null}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
